import {
  Button,
  FlexBox,
  Modal,
  space,
  theme,
  ThresholdInputs,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import i18n, { t } from "i18next";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styledComponent from "styled-components";

import { ReactComponent as ArrowForwardIcon } from "../../public/material-icons/arrow-forward.svg";
import { ReactComponent as LabelIcon } from "../../public/material-icons/label-outline.svg";
import { ReactComponent as InfoIcon } from "../../public/material-icons/wb-incandescent.svg";
import {
  type FunctionOverrides,
  receiveLADSDevice,
  receiveLADSFunction,
  updateLADSFunctionOverrides,
} from "../api/lads";
import { useAuthContext } from "../auth-provider";
import { tripleFromBrowsePath } from "../util/browse-path";
import { formatValue } from "../util/channel-data.tsx";
import type { Channel } from "../view-model";

const Separator = styledComponent.div`
  border-top: 1px solid #e0e0e0;
  margin: ${space(2)} 0;
`;

// Dialogbreite 630px (Modal width ist nur min-width; ohne Deckel bestimmt
// der längste Beschreibungssatz die Breite). 630 − 2×24 Panel-Padding.
const DialogBody = styledComponent.div`
  width: 582px;
  max-width: 100%;
`;

const DisabledFieldset = styledComponent.fieldset`
  border: none;
  padding: 0;
  margin: 0;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;

const FixInfoIcon = styledComponent(InfoIcon)`
  flex-grow: 0;
  flex-shrink: 0;
`;

// Volle Dialogbreite, damit die Beschreibungs-Slots den Platz links der
// Kachel-Spalte füllen.
const FullWidthThresholdInputs = styledComponent(ThresholdInputs)`
  width: 100%;
`;

// Sensor Device Info: two label/value pairs per row, eingerückt unter der
// Überschrift (Figma 5208-28225)
const InfoGrid = styledComponent.div`
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  column-gap: ${space(3)};
  row-gap: ${space(2)};
  align-items: center;
  padding-left: ${space(6)};
`;

const InfoLabel = styledComponent.span`
  display: inline-flex;
  align-items: center;
  gap: ${space(1)};
  font-size: ${theme.font.size.default.sm};
  font-weight: ${theme.font.weight.semiBold};
  color: ${theme.color.black};
  white-space: nowrap;
  svg {
    width: ${space(4)};
    height: ${space(4)};
    fill: ${theme.colorPalette.grey400};
  }
`;

const InfoValue = styledComponent.span`
  font-size: ${theme.font.size.default.sm};
  color: ${theme.color.black};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OpenLink = styledComponent.button`
  display: inline-flex;
  align-items: center;
  gap: ${space(1)};
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.color.info};
  font-size: ${theme.font.size.default.sm};
  font-weight: ${theme.font.weight.semiBold};
  svg {
    width: ${space(4)};
    height: ${space(4)};
    fill: currentColor;
  }
`;

type ChannelThresholdConfigProps = {
  channel: Channel;
  deviceId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful save - parent should reload channel data */
  onSave?: () => void;
};

/**
 * Parse channel ID to extract LADS path components
 * Channel ID format: "deviceId/functionalUnitId/functionId"
 */
function parseChannelId(channelId: string): {
  deviceId: string;
  functionalUnitId: string;
  functionId: string;
} | null {
  const parts = channelId.split("/");
  if (parts.length < 3) {
    return null;
  }
  return {
    deviceId: parts[0],
    functionalUnitId: parts[1],
    functionId: parts[2],
  };
}

type SourceInfo = {
  deviceName?: string;
  serial?: string;
  functionName?: string;
  port?: string;
};

export const ChannelThresholdConfig: FC<ChannelThresholdConfigProps> = ({
  channel,
  deviceId: _deviceId,
  isOpen,
  onClose,
  onSave,
}) => {
  const { accessToken } = useAuthContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [source, setSource] = useState<SourceInfo>({});
  const [config, setConfig] = useState({
    highAlert: "",
    highWarning: "",
    lowWarning: "",
    lowAlert: "",
    delay: "",
    offDelay: "",
  });

  // Canonical browsePath is the data-request triple (mount-safe — channel.id
  // is the ambiguous mount triple); id-parse only for legacy channels.
  const parsed = channel.browsePath
    ? tripleFromBrowsePath(channel.browsePath)
    : parseChannelId(channel.id);

  // Derive read-only state from userAccessLevel (OPC UA style)
  const isReadOnly = (() => {
    const acl = channel.alarmMonitor?.userAccessLevel;
    // Writable if userAccessLevel includes 'currentWrite', otherwise read-only
    return acl ? !acl.includes("currentWrite") : false;
  })();

  // Fetch fresh alarmMonitor + sensor-device info when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Backend onDelay/offDelay are seconds (per OpenAPI). Form is in minutes.
    const secToMin = (s: unknown): string =>
      typeof s === "number" && Number.isFinite(s) && s > 0
        ? (s / 60).toString()
        : "";
    const applyLimits = (limits: {
      highHighLimit?: number;
      highLimit?: number;
      lowLimit?: number;
      lowLowLimit?: number;
      onDelay?: number;
      offDelay?: number;
    }) => {
      setConfig({
        highAlert: limits.highHighLimit?.toString() ?? "",
        highWarning: limits.highLimit?.toString() ?? "",
        lowWarning: limits.lowLimit?.toString() ?? "",
        lowAlert: limits.lowLowLimit?.toString() ?? "",
        delay: secToMin(limits.onDelay),
        offDelay: secToMin(limits.offDelay),
      });
    };

    // Serial doubles as browse-name fallback ("<serial>@<serial>")
    const fallbackSerial = parsed?.deviceId.split("@")[0];
    setSource({
      serial: fallbackSerial,
      functionName: parsed?.functionId,
      port: channel.port,
    });

    if (!parsed) {
      applyLimits(channel.alarmMonitor || {});
      return;
    }

    // Always fetch fresh data from API
    receiveLADSFunction(
      parsed.deviceId,
      parsed.functionalUnitId,
      parsed.functionId,
      accessToken ?? undefined
    )
      .then((func) => {
        applyLimits((func as any)?.alarmMonitor || {});
        setSource((prev) => ({
          ...prev,
          // Raw source label on purpose (serial is part of the LADS function
          // name) — NOT the componentName override shown as the modal title.
          functionName:
            func.displayName || func.browseName || prev.functionName,
          port: func.port ?? prev.port,
        }));
      })
      .catch(() => {
        applyLimits(channel.alarmMonitor || {});
      });

    receiveLADSDevice(parsed.deviceId, accessToken ?? undefined)
      .then((device) => {
        setSource((prev) => ({
          ...prev,
          deviceName:
            device.componentName || device.displayName || device.browseName,
          serial: device.identification?.serialNumber || prev.serial,
        }));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, channel.id]);

  const handleSubmit = async () => {
    if (!parsed) return;

    setIsLoading(true);
    try {
      // Build overrides — null clears a limit, undefined leaves it untouched.
      // Delay ist im Formular in Minuten, OpenAPI erwartet Sekunden → *60.
      // Leer im UI heisst "kein Delay" (= 0s, was auch dem Server-Default
      // entspricht), nicht "Wert nicht anfassen" — sonst behaelt der Server
      // den zuletzt gespeicherten Wert.
      const minToSec = (s: string): number => (s === "" ? 0 : Number(s) * 60);
      const overrides: Partial<FunctionOverrides> = {
        highHighLimit: config.highAlert ? Number(config.highAlert) : null,
        highLimit: config.highWarning ? Number(config.highWarning) : null,
        lowLimit: config.lowWarning ? Number(config.lowWarning) : null,
        lowLowLimit: config.lowAlert ? Number(config.lowAlert) : null,
        onDelay: minToSec(config.delay),
        offDelay: minToSec(config.offDelay),
        enabledState: true,
      };

      // Save to LADS API
      await updateLADSFunctionOverrides(
        parsed.deviceId,
        parsed.functionalUnitId,
        parsed.functionId,
        overrides,
        accessToken ?? undefined
      );

      // Refetch units and close
      onSave?.();
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[ChannelThresholdConfig] Failed to save overrides:",
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const openSourceDevice = () => {
    if (!parsed) return;
    onClose();
    navigate(`/workplaces/${encodeURIComponent(parsed.deviceId)}`);
  };

  return (
    <Modal onClose={onClose} open={isOpen} width="630px">
      <DialogBody>
        <FlexBox paddingBottom={space(2)}>
          <Typography size="xl" weight="semiBold">
            {i18n.exists(`channel.${channel.name}`)
              ? t(`channel.${channel.name}`)
              : channel.name}
          </Typography>
        </FlexBox>
        <Separator />

        {/* Sensor Device Info (Figma 5208-28201) */}
        <FlexBox flexDirection="column" gap={space(3)} paddingBottom={space(4)}>
          <FlexBox alignItems="center" justifyContent="space-between">
            <Typography weight="semiBold">
              {t("channel.threshold.sensorInfo")}
            </Typography>
            <OpenLink onClick={openSourceDevice} type="button">
              <ArrowForwardIcon /> {t("channel.threshold.open")}
            </OpenLink>
          </FlexBox>
          <InfoGrid>
            <InfoLabel>
              <LabelIcon /> {t("channel.threshold.sourceName")}
            </InfoLabel>
            <InfoValue title={source.deviceName}>
              {source.deviceName ?? "—"}
            </InfoValue>
            <InfoLabel>
              <LabelIcon /> {t("channel.threshold.sourceFunction")}
            </InfoLabel>
            <InfoValue title={source.functionName}>
              {source.functionName ?? "—"}
            </InfoValue>
            <InfoLabel>
              <LabelIcon /> {t("channel.threshold.sourceSerial")}
            </InfoLabel>
            <InfoValue title={source.serial}>{source.serial ?? "—"}</InfoValue>
            <InfoLabel>
              <LabelIcon /> {t("channel.threshold.sourcePort")}
            </InfoLabel>
            <InfoValue title={source.port}>{source.port ?? "—"}</InfoValue>
          </InfoGrid>
        </FlexBox>

        <FlexBox paddingBottom={space(2)}>
          <Typography weight="semiBold">
            {t("channel.threshold.alarmSettings")}
          </Typography>
        </FlexBox>
        {isReadOnly && (
          <FlexBox padding={`0 ${space(8)} ${space(4)}`}>
            <Typography color="grey500" size="sm">
              {t("channel.threshold.readOnly")}
            </Typography>
          </FlexBox>
        )}
        <DisabledFieldset disabled={isReadOnly}>
          <FlexBox padding={`${space(4)} ${space(8)} ${space(8)}`}>
            {/* Beschreibungen als Zeilen-Slots der ThresholdInputs — so stehen
              sie garantiert auf Höhe des zugeordneten Abschnitts. */}
            <FullWidthThresholdInputs
              descriptions={{
                highAlert: (
                  <FlexBox gap={space(2)}>
                    <FixInfoIcon fontSize={space(6)} />
                    <Typography size="base">
                      {t("channel.threshold.highAlert")}
                    </Typography>
                  </FlexBox>
                ),
                highWarning: (
                  <FlexBox gap={space(2)}>
                    <FixInfoIcon fontSize={space(6)} />
                    <Typography size="base">
                      {t("channel.threshold.highWarning")}
                    </Typography>
                  </FlexBox>
                ),
                inRange: (
                  <FlexBox
                    flexDirection="column"
                    gap={space(1)}
                    paddingLeft={space(6)}
                  >
                    <Typography size="base" weight="semiBold">
                      {t("channel.threshold.currentValue")}
                    </Typography>
                    <Typography color="blue500" size="xl" weight="semiBold">
                      {formatValue(
                        channel.current?.value,
                        channel.unit,
                        channel.name
                      )}
                    </Typography>
                  </FlexBox>
                ),
                lowWarning: (
                  <FlexBox gap={space(2)}>
                    <FixInfoIcon fontSize={space(6)} />
                    <Typography size="base">
                      {t("channel.threshold.lowWarning")}
                    </Typography>
                  </FlexBox>
                ),
                lowAlert: (
                  <FlexBox gap={space(2)}>
                    <FixInfoIcon fontSize={space(6)} />
                    <Typography size="base">
                      {t("channel.threshold.lowAlert")}
                    </Typography>
                  </FlexBox>
                ),
                // one hint for both delay rows (↑ on / ↓ off), per Figma
                delay: (
                  <FlexBox gap={space(2)}>
                    <FixInfoIcon fontSize={space(6)} />
                    <Typography size="base">
                      {t("channel.threshold.delayHint")}
                    </Typography>
                  </FlexBox>
                ),
              }}
              onChange={(v) =>
                setConfig({
                  highAlert: v.highAlert ?? "",
                  highWarning: v.highWarning ?? "",
                  lowWarning: v.lowWarning ?? "",
                  lowAlert: v.lowAlert ?? "",
                  delay: v.delay ?? "",
                  offDelay: v.offDelay ?? "",
                })
              }
              targetValue={
                channel.targetValue?.value !== undefined &&
                channel.targetValue?.value !== null
                  ? String(channel.targetValue.value)
                  : undefined
              }
              targetValueLabel={
                // Soll-Wert nur für Control Functions bzw. wenn die Function
                // tatsächlich einen TargetValue hat — reine Sensoren zeigen
                // nur die grüne In-Range-Kachel.
                channel.controllable || channel.targetValue?.value != null
                  ? t("channel.threshold.targetValue") ?? undefined
                  : undefined
              }
              unit={channel.unit}
              values={config}
            />
          </FlexBox>
        </DisabledFieldset>
        <FlexBox gap={space(2)} justifyContent="flex-end">
          <Button onClick={onClose} variant="secondary">
            {t("common.close")}
          </Button>
          <Button disabled={isReadOnly || isLoading} onClick={handleSubmit}>
            {isLoading ? t("common.saving") : t("common.save")}
          </Button>
        </FlexBox>
      </DialogBody>
    </Modal>
  );
};
